import { h, Fragment } from "preact";
import { useState, useEffect, useRef, useMemo } from "preact/hooks";
import useProduct from "../../helpers/useProduct/useProduct";
import unifyGroupOptions from "../../helpers/unifyGroupOptions";
import unifyGroupVariants from "../../helpers/unifyGroupVariants";
import { IdPrefixContext } from "../../helpers/idPrefixContext";
import ProductOption from "./product-option";
import ProductOptionProperties from "./product-option-properties";
import variantHasOptionValue from "../../helpers/variantHasOptionValue";
import ProductDiscontinued from "./product-discontinued";
import SegmentedToggle from "../segmented-toggle/segmented-toggle";
import displayableOptionValues from "./displayableOptionValues";
import resolveOptionSelection from "./resolveOptionSelection";
import { isImagePillOption, isSwatchOption } from "./product-option-styles";
import SwatchDetailsDrawer from "../swatch-details-drawer/swatch-details-drawer";
import swatchDrawerItems from "../swatch-details-drawer/swatchDrawerItems";
import {
  STOCK_FILTERS,
  variantStockStatuses,
  filterVariantsByStock,
  availableStockFilters,
  pickClosestVariant,
} from "./stockFilters";

const optionValueExists = ({ options, optionName, optionValue }) =>
  options.some(
    (option) =>
      option.optionName === optionName &&
      option.optionValues.some(({ name }) => optionValue === name),
  );

const OPTION_ROLES = {
  primary: "primary",
  secondary: "secondary",
  linked: "linked",
  isolated: "isolated",
};

const LAYOUTS = {
  // All / Ready to Ship / Made For You toggle, materials shown ungrouped
  default: "default",
  // All / Ready to Ship / Made For You toggle, materials grouped by fabric
  allMto: "all-mto",
  // All / Ready to Ship toggle, materials grouped by fabric inside
  // Standard / Made For You sections
  all: "all",
};

const LAYOUT_FILTERS = {
  [LAYOUTS.default]: [
    STOCK_FILTERS.all,
    STOCK_FILTERS.readyToShip,
    STOCK_FILTERS.madeForYou,
  ],
  [LAYOUTS.allMto]: [
    STOCK_FILTERS.all,
    STOCK_FILTERS.readyToShip,
    STOCK_FILTERS.madeForYou,
  ],
  [LAYOUTS.all]: [STOCK_FILTERS.all, STOCK_FILTERS.readyToShip],
};

const MATERIAL_GROUP_LAYOUTS = [LAYOUTS.allMto, LAYOUTS.all];

const MADE_TO_ORDER_SPLIT_LAYOUTS = [LAYOUTS.all];

const filterLabelKeys = {
  [STOCK_FILTERS.all]: "stock_filter_all",
  [STOCK_FILTERS.readyToShip]: "stock_filter_ready_to_ship",
  [STOCK_FILTERS.madeForYou]: "stock_filter_made_for_you",
  [STOCK_FILTERS.sale]: "stock_filter_sale",
  [STOCK_FILTERS.contract]: "stock_filter_contract",
};

/**
 * The layout's filters, with a collection filter taking Ready to Ship's place
 * just after All.
 */
const segmentFilters = ({ layout, collectionFilter }) => {
  const layoutFilters = LAYOUT_FILTERS[layout] || [];

  if (!collectionFilter || !layoutFilters.length) return layoutFilters;

  const [all, ...rest] = layoutFilters.filter(
    (filter) => filter !== STOCK_FILTERS.readyToShip,
  );

  return [all, collectionFilter, ...rest];
};

export default function ProductOptions({
  productId,
  idPrefix,
  initialVariantId,
  initialVariantHidden,
  initialFirstOptionValue,
  optionsRole = null,
  enableSwatchPreview,
  enableAccordions,
  quantityRequested,
  enableAvailabilityPreview,
  onVariantChanged,
  optionSize = "default",
  disabledSwatchStyle,
  unavailableStockStatuses,
  waitlistStockStatuses,
  layout = null,
  collectionFilter = null,
}) {
  const [productData] = useProduct(productId);
  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);
  const [selectedOptionValues, setSelectedOptionValues] = useState({});
  const [lastChangedOption, setLastChangedOption] = useState(null);
  const [swatchPreviewIndex, setSwatchPreviewIndex] = useState(0);
  const [stockFilter, setStockFilter] = useState(STOCK_FILTERS.all);
  const ref = useRef(null);
  const listRef = useRef(null);
  const stockFilterRef = useRef(null);
  const stockFilterHolderRef = useRef(null);

  const statuses = useMemo(
    () => variantStockStatuses({ variants, quantity: quantityRequested }),
    [variants, quantityRequested],
  );

  // The active filter narrows the variant set, and everything below builds the
  // form from it as though it were the whole product.
  const eligibleVariants = useMemo(
    () => filterVariantsByStock({ variants, statuses, filter: stockFilter }),
    [variants, statuses, stockFilter],
  );

  const stockFilteredOptions = useMemo(() => {
    if (stockFilter === STOCK_FILTERS.all) return options;

    return options.map((option) => ({
      ...option,
      optionValues: option.optionValues.filter((optionValue) =>
        eligibleVariants.some((variant) =>
          variantHasOptionValue({
            variant,
            optionName: option.optionName,
            optionValue: optionValue.name,
          }),
        ),
      ),
    }));
  }, [options, eligibleVariants, stockFilter]);

  const variantForSelection = (selection) =>
    eligibleVariants.find((variant) =>
      variant.selectedOptions.every((selectedOption) => {
        return selection[selectedOption.name] === selectedOption.value;
      }),
    ) || null;

  // Read off the untouched selection: the narrowing below needs to know whether
  // the form is frozen on a discontinued variant before it can run.
  const rawSelectedVariant = variantForSelection(selectedOptionValues);

  const selectedIsHidden =
    rawSelectedVariant && JSON.parse(rawSelectedVariant.hideOnPdp);

  const { options: visibleOptions, selection: resolvedOptionValues } = useMemo(
    () =>
      resolveOptionSelection({
        options: stockFilteredOptions.map((option) => ({
          ...option,
          optionValues: displayableOptionValues({
            optionValues: option.optionValues,
            selectedOptionValue: selectedOptionValues[option.optionName],
            selectedIsHidden,
          }),
        })),
        variants: eligibleVariants,
        selection: selectedOptionValues,
        anchor: lastChangedOption,
      }),
    [
      stockFilteredOptions,
      eligibleVariants,
      selectedOptionValues,
      selectedIsHidden,
      lastChangedOption,
    ],
  );

  // State holds what the shopper asked for; this is what the variants allow, so
  // everything downstream reads it rather than the raw selection.
  const selectedVariant = variantForSelection(resolvedOptionValues);

  const selectedVariantId = selectedVariant?.id;

  const {
    unavailableOptionValues,
    waitlistOptionValues,
    variantsByOptionValue,
  } = useMemo(() => {
    const newUnavailableOptionValues = {};
    const newWaitlistOptionValues = {};
    const newVariantsByOptionValue = {};

    visibleOptions.forEach((option) => {
      const newOptionValues = option.optionValues.map((optionValue) => {
        // Find variant that matches this option value and all other selected option values
        // Value available is that variant's stock status

        const optionValuesCombination = {
          ...resolvedOptionValues,
          [option.optionName]: optionValue.name,
        };

        const matchingVariant = eligibleVariants.find((variant) =>
          Object.entries(optionValuesCombination).every(
            ([optionName, optionValue]) =>
              variantHasOptionValue({ variant, optionName, optionValue }),
          ),
        );

        if (!matchingVariant) return false;

        const stockStatus = statuses.get(matchingVariant.id);

        return {
          unavailable: unavailableStockStatuses.includes(stockStatus),
          waitlist: waitlistStockStatuses.includes(stockStatus),
          variant: matchingVariant,
          optionValue: optionValue.name,
        };
      });

      if (enableAvailabilityPreview) {
        newUnavailableOptionValues[option.optionName] = newOptionValues
          .filter(({ unavailable }) => unavailable)
          .map(({ optionValue }) => optionValue);
      }

      newWaitlistOptionValues[option.optionName] = newOptionValues
        .filter(({ waitlist }) => waitlist)
        .map(({ optionValue }) => optionValue);

      newVariantsByOptionValue[option.optionName] = newOptionValues;
    });

    return {
      unavailableOptionValues: newUnavailableOptionValues,
      waitlistOptionValues: newWaitlistOptionValues,
      variantsByOptionValue: newVariantsByOptionValue,
    };
  }, [
    resolvedOptionValues,
    eligibleVariants,
    visibleOptions,
    statuses,
    enableAvailabilityPreview,
    unavailableStockStatuses,
    waitlistStockStatuses,
  ]);

  const updateOptionValue = (newValue, optionName, fromLinked) => {
    if (
      !optionValueExists({
        options: stockFilteredOptions,
        optionName,
        optionValue: newValue,
      })
    )
      return;

    if (
      optionsRole === OPTION_ROLES.primary ||
      (optionsRole === OPTION_ROLES.linked && !fromLinked)
    ) {
      ref?.current.dispatchEvent(
        new CustomEvent("variant:option-changed", {
          bubbles: true,
          detail: {
            optionName,
            optionValue: newValue,
            optionsRole,
          },
        }),
      );
    }

    setLastChangedOption(optionName);

    const newSelectedOptionValues = {
      ...resolvedOptionValues,
    };

    newSelectedOptionValues[optionName] = newValue;
    setSelectedOptionValues(newSelectedOptionValues);
  };

  useEffect(() => {
    const unifiedVariants = unifyGroupVariants(
      productData,
      initialVariantHidden,
    );

    setVariants(unifiedVariants);
    setLastChangedOption(null);

    const unifiedOptions = unifyGroupOptions(productData);

    setSwatchPreviewIndex(
      unifiedOptions.findIndex(({ optionValues }) =>
        optionValues.some((optionValue) => optionValue.swatch),
      ),
    );
    setOptions(unifiedOptions);

    let initialVariant = null;

    // Try to find a matching variant (for add on products)
    if (initialFirstOptionValue && unifiedOptions.length) {
      initialVariant = unifiedVariants.find((variant) =>
        variantHasOptionValue({
          variant,
          optionName: unifiedOptions[0].optionName,
          optionValue: initialFirstOptionValue,
        }),
      );
    }

    if (!initialVariant) {
      initialVariant = unifiedVariants.find(
        (variant) => variant.id === initialVariantId,
      );
    }

    // Arriving from a collection type opens on its filter, and on a variant
    // inside it. Left on All when nothing matches, as the filter's segment
    // won't show.
    const collectionVariants = collectionFilter
      ? filterVariantsByStock({
          variants: unifiedVariants,
          filter: collectionFilter,
        })
      : [];

    if (collectionVariants.length) setStockFilter(collectionFilter);

    const openingVariants = collectionVariants.length
      ? collectionVariants
      : unifiedVariants;

    if (
      (initialVariant || collectionVariants.length) &&
      !openingVariants.includes(initialVariant)
    ) {
      initialVariant = pickClosestVariant({
        variants: openingVariants,
        selectedOptionValues: Object.fromEntries(
          (initialVariant?.selectedOptions || []).map(({ name, value }) => [
            name,
            value,
          ]),
        ),
      });
    }

    if (initialVariant) {
      setSelectedOptionValues(
        Object.fromEntries(
          initialVariant.selectedOptions.map((option) => [
            option.name,
            option.value,
          ]),
        ),
      );
    } else {
      // Select first option value
      setSelectedOptionValues(
        Object.fromEntries(
          unifiedOptions.map((option) => [
            option.optionName,
            option.optionValues[0].name,
          ]),
        ),
      );
    }
  }, [productData, setSelectedOptionValues, initialVariantId]);

  useEffect(() => {
    if (!ref || !variants.length) return;

    const detail = {
      variant: selectedVariant,
      optionsRole,
    };

    if (typeof onVariantChanged === "function") {
      onVariantChanged(detail);
    } else {
      ref.current.dispatchEvent(
        new CustomEvent("variant:changed", {
          bubbles: true,
          detail,
        }),
      );
    }
  }, [selectedVariant, variants, ref]);

  useEffect(() => {
    if (
      optionsRole === OPTION_ROLES.primary ||
      optionsRole === OPTION_ROLES.isolated
    )
      return;

    const handlePrimaryVariantOptionChange = (e) => {
      if (
        optionsRole === OPTION_ROLES.secondary &&
        e.detail.optionsRole !== OPTION_ROLES.primary
      )
        return;

      if (
        optionsRole === OPTION_ROLES.linked &&
        e.detail.optionsRole !== OPTION_ROLES.linked
      )
        return;

      const { optionName, optionValue } = e.detail;

      updateOptionValue(
        optionValue,
        optionName,
        e.detail.optionsRole === OPTION_ROLES.linked,
      );
    };

    document.addEventListener(
      "variant:option-changed",
      handlePrimaryVariantOptionChange,
    );

    return () =>
      document.removeEventListener(
        "variant:option-changed",
        handlePrimaryVariantOptionChange,
      );
  }, [updateOptionValue, optionsRole]);

  const segments = useMemo(() => {
    const layoutFilters = segmentFilters({ layout, collectionFilter });

    if (!layoutFilters.length) return [];

    // Nothing to filter down to with a single variant
    if (variants.length < 2) return [];

    const available = availableStockFilters({ variants, statuses });
    const usable = layoutFilters.filter((filter) => available.has(filter));

    // A lone "All" is not a choice
    if (usable.length < 2) return [];

    return usable.map((filter) => ({
      value: filter,
      label: window.localeStrings.product[filterLabelKeys[filter]],
      icon:
        filter === STOCK_FILTERS.madeForYou
          ? window.theme.madeToOrderIcon
          : null,
    }));
  }, [layout, collectionFilter, variants, statuses]);

  useEffect(() => {
    if (stockFilter === STOCK_FILTERS.all) return;
    if (segments.some(({ value }) => value === stockFilter)) return;

    // The filter's segment went away — product data reloaded, or quantity moved
    // the last matching variant out of range.
    setStockFilter(STOCK_FILTERS.all);
  }, [segments, stockFilter]);

  const changeStockFilter = (newFilter) => {
    const newEligibleVariants = filterVariantsByStock({
      variants,
      statuses,
      filter: newFilter,
    });

    const selectionSurvives = newEligibleVariants.some(
      ({ id }) => id === selectedVariantId,
    );

    if (!selectionSurvives) {
      const closest = pickClosestVariant({
        variants: newEligibleVariants,
        selectedOptionValues: resolvedOptionValues,
      });

      if (closest) {
        setSelectedOptionValues(
          Object.fromEntries(
            closest.selectedOptions.map(({ name, value }) => [name, value]),
          ),
        );
      }
    }

    // Stale against the incoming variant set; repopulated on the next selection
    setLastChangedOption(null);
    setStockFilter(newFilter);
  };

  // A pill option down to one value is no longer a choice, so products can opt
  // into dropping it. Swatches and image pills stay whatever their count, since
  // they also show what was picked.
  const hideSingleValueOptions = JSON.parse(
    productData?.hideSingleVariantOptions || "false",
  );

  const displayedOptions = useMemo(
    () =>
      visibleOptions
        .map((option, index) => ({ option, index }))
        .filter(
          ({ option }) =>
            !hideSingleValueOptions ||
            option.optionValues.length > 1 ||
            isSwatchOption(option) ||
            isImagePillOption(option),
        ),
    [visibleOptions, hideSingleValueOptions],
  );

  // The stock filter and the option summaries pin as one stack: each below the
  // ones already stuck, and all of them letting go together as the options end.
  // Both halves come off the one pass — the offset a member pins at, and how far
  // past its own content its holder has to reach so that it doesn't let go
  // before the last summary does.
  useEffect(() => {
    const list = listRef.current;

    if (!list) return;

    const options = Array.from(list.children)
      .map((element) => ({
        element,
        pinned: element.querySelector(".product-options__option-summary"),
      }))
      .filter(({ pinned }) => pinned);

    const stack = stockFilterHolderRef.current
      ? [
          {
            element: stockFilterHolderRef.current,
            pinned: stockFilterRef.current,
          },
          ...options,
        ]
      : options;

    if (!stack.length) return;

    // Where the options end, and with them the whole stack. Read off the list
    // rather than off the last member, so that a filter with no accordions
    // under it still reaches the bottom.
    const optionsEnd = () =>
      list.getBoundingClientRect().bottom -
      (parseFloat(getComputedStyle(list).borderBottomWidth) || 0);

    // The bottom the holder would have without the spacer inside it, which the
    // flow is given straight back and so never moved anything.
    const naturalBottom = ({ element }) =>
      element.getBoundingClientRect().bottom -
      (parseFloat(element.style.getPropertyValue("--stack-trail")) || 0);

    const setStack = () => {
      // Measured off the rect rather than offsetHeight so a member pins flush to
      // the bottom of the one above it, not a rounded fraction off.
      const heights = stack.map(
        ({ pinned }) => pinned.getBoundingClientRect().height,
      );
      const bottoms = stack.map(naturalBottom);
      const end = optionsEnd();

      let above = 0;
      let below = heights.reduce((total, height) => total + height, 0);

      stack.forEach(({ element, pinned }, index) => {
        below -= heights[index];

        pinned.style.setProperty("--stack-offset", `${above}px`);
        above += heights[index];

        // That end, less the stack gathered below this member by the time it
        // gets there.
        const trail = `${Math.max(0, end - below - bottoms[index])}px`;

        if (element.style.getPropertyValue("--stack-trail") !== trail) {
          element.style.setProperty("--stack-trail", trail);
        }
      });
    };

    setStack();

    // Watches what each member pins rather than the member itself: the spacer
    // written above resizes the member, and resizing a watched box from inside
    // the callback is a ResizeObserver loop error. Anything else that moves a
    // member's bottom resizes the list too, since the spacer is taken back off
    // the flow.
    const observer = new ResizeObserver(setStack);
    stack.forEach(({ pinned }) => observer.observe(pinned));
    // Where the stack lets go, which nothing in it need have moved to change
    observer.observe(list);

    return () => observer.disconnect();
  }, [displayedOptions, segments.length]);

  // The swatch details drawer works off the whole colour option, not just the
  // values on show, so it reads the option out of the same parallel maps the
  // option list does rather than taking it as a prop.
  const swatchDrawerOption = visibleOptions[swatchPreviewIndex] || null;

  const drawerItems = useMemo(
    () =>
      swatchDrawerItems({
        option: swatchDrawerOption,
        variantsByOptionValue: swatchDrawerOption
          ? variantsByOptionValue[swatchDrawerOption.optionName]
          : [],
        selectedOptionValue: swatchDrawerOption
          ? resolvedOptionValues[swatchDrawerOption.optionName]
          : null,
      }),
    [swatchDrawerOption, variantsByOptionValue, resolvedOptionValues],
  );

  // The sizing note belongs to the size-style option — the first non-swatch one
  const sizeNoteIndex = displayedOptions.findIndex(
    ({ option }) => !isSwatchOption(option),
  );

  const sizeNote = selectedVariant?.sizeNote;

  const variantMediaImage = selectedVariant?.media?.[0]?.image || null;

  const badge = selectedVariant
    ? selectedVariant.badge || selectedVariant.product.badge
    : null;

  return (
    <div
      className={`product-options ${variants.length ? "product-options--loaded" : ""}`}
      ref={ref}
    >
      {segments.length > 0 && (
        <div
          className="product-options__stock-filter-holder"
          ref={stockFilterHolderRef}
        >
          <div className="product-options__stock-filter" ref={stockFilterRef}>
            <SegmentedToggle
              name={`${idPrefix}stock-filter`}
              legend={
                window.localeStrings.product.stock_filter_accessible_label
              }
              segments={segments}
              value={stockFilter}
              onChange={(e) => changeStockFilter(e.target.value)}
            />
          </div>
        </div>
      )}
      {selectedVariant && (
        <input
          type="hidden"
          name="id"
          value={window.gidToId(selectedVariant.id)}
        />
      )}
      {selectedVariant && selectedVariant.retailPrice && (
        <input
          type="hidden"
          name="properties[_retail_price]"
          value={window.formatMoney(selectedVariant.retailPrice, false, "USD")}
        />
      )}
      {selectedVariant && (
        <input
          type="hidden"
          name="properties[_sale_item]"
          value={!!selectedVariant.saleItem}
        />
      )}
      {selectedVariant && selectedVariant.finalSale && (
        <input
          type="hidden"
          name="properties[_final_sale]"
          value={selectedVariant.finalSale}
        />
      )}
      {selectedVariant && (
        <input
          type="hidden"
          name="_product_gid"
          value={selectedVariant.product.id}
        />
      )}
      {selectedVariant && JSON.parse(selectedVariant.madeToOrder) && (
        <input type="hidden" name="properties[_made_to_order]" value="true" />
      )}
      {badge && JSON.parse(badge.showInCart) && (
        <>
          <input type="hidden" name="properties[_badge]" value={badge.text} />
          <input
            type="hidden"
            name="properties[_badge_color]"
            value={badge.color}
          />
          <input
            type="hidden"
            name="properties[_badge_position]"
            value={badge.position}
          />
        </>
      )}
      <ProductOptionProperties variant={selectedVariant} />
      <IdPrefixContext.Provider value={idPrefix}>
        <div
          className={`product-options__list ${enableAccordions ? "product-options__list--accordions" : ""}`}
          ref={listRef}
        >
          {displayedOptions.map(({ option, index }, i) => (
            <ProductOption
              key={option.optionName}
              option={option}
              optionNumber={displayedOptions.length > 1 ? i + 1 : null}
              updateOptionValue={(newValue) =>
                updateOptionValue(newValue, option.optionName)
              }
              selectedOptionValue={resolvedOptionValues[option.optionName]}
              unavailableOptionValues={
                unavailableOptionValues[option.optionName]
              }
              waitlistOptionValues={waitlistOptionValues[option.optionName]}
              variantsByOptionValue={variantsByOptionValue[option.optionName]}
              allowMaterialGroups={
                index === swatchPreviewIndex &&
                MATERIAL_GROUP_LAYOUTS.includes(layout)
              }
              splitMadeToOrder={MADE_TO_ORDER_SPLIT_LAYOUTS.includes(layout)}
              openByDefault
              last={i === displayedOptions.length - 1}
              selectedIsHidden={selectedIsHidden}
              selectedVariant={selectedVariant}
              enableSwatchPreview={
                enableSwatchPreview && index === swatchPreviewIndex
              }
              enableAccordions={enableAccordions}
              optionSize={optionSize}
              disabledSwatchStyle={disabledSwatchStyle}
              sizeNote={i === sizeNoteIndex ? sizeNote : null}
            />
          ))}
        </div>
      </IdPrefixContext.Provider>
      {selectedIsHidden && (
        <ProductDiscontinued product={productData} variants={variants} />
      )}
      {variants.length > 0 && !selectedVariant && (
        <div
          className="product-options__unavailable"
          dangerouslySetInnerHTML={{
            __html: window.localeStrings.product.unavailable_combination,
          }}
        ></div>
      )}
      {variants.length === 0 && (
        <div className="product-options__skeleton"></div>
      )}
      {enableSwatchPreview && swatchDrawerOption && (
        <SwatchDetailsDrawer
          items={drawerItems}
          optionName={swatchDrawerOption.optionName}
          productTitle={productData?.title}
          idPrefix={idPrefix}
          variantImage={variantMediaImage}
          updateOptionValue={(newValue) =>
            updateOptionValue(newValue, swatchDrawerOption.optionName)
          }
        />
      )}
    </div>
  );
}
